export const TELEMETRY_ALLOWED_HOST_SUFFIXES = ['agentnoon.co', 'dayforcehcm.com']
export const TELEMETRY_MAX_BYTES = 5 * 1024 * 1024

const TELEMETRY_STORAGE_KEY = 'agentnoon-telemetry'
const encoder = new TextEncoder()

function getStorage() {
  try {
    return globalThis.sessionStorage
  } catch {
    return null
  }
}

function toSerializable(value) {
  const seen = new WeakSet()

  try {
    return JSON.parse(
      JSON.stringify(value, (_key, nestedValue) => {
        if (nestedValue instanceof Error) {
          return {
            name: nestedValue.name,
            message: nestedValue.message,
            stack: nestedValue.stack
          }
        }

        if (typeof nestedValue === 'bigint') return nestedValue.toString()
        if (typeof nestedValue !== 'object' || nestedValue === null) return nestedValue
        if (seen.has(nestedValue)) return '[Circular]'
        seen.add(nestedValue)
        return nestedValue
      })
    )
  } catch {
    return String(value)
  }
}

function serializedSize(value) {
  return encoder.encode(JSON.stringify(value)).byteLength
}

function fitEntry(entry) {
  if (serializedSize([entry]) <= TELEMETRY_MAX_BYTES) return entry

  const fittedEntry = { ...entry }
  const omittedMessage = '[omitted: telemetry entry exceeds storage limit]'
  const bodyFields = ['args', 'body', 'requestBody', 'responseBody']
  bodyFields.forEach((field) => {
    if (field in fittedEntry) fittedEntry[field] = omittedMessage
  })

  if (serializedSize([fittedEntry]) <= TELEMETRY_MAX_BYTES) return fittedEntry

  return {
    timestamp: entry.timestamp,
    type: entry.type,
    method: entry.method,
    url: entry.url,
    status: entry.status,
    omitted: omittedMessage
  }
}

function readStoredEntries() {
  const storage = getStorage()
  if (!storage) return []

  try {
    const entries = JSON.parse(storage.getItem(TELEMETRY_STORAGE_KEY) || '[]')
    return Array.isArray(entries) ? entries : []
  } catch {
    return []
  }
}

function writeStoredEntries(entries) {
  const storage = getStorage()
  if (!storage) return

  while (entries.length && serializedSize(entries) > TELEMETRY_MAX_BYTES) {
    entries.shift()
  }

  while (entries.length) {
    try {
      storage.setItem(TELEMETRY_STORAGE_KEY, JSON.stringify(entries))
      return
    } catch {
      entries.shift()
    }
  }

  try {
    storage.removeItem(TELEMETRY_STORAGE_KEY)
  } catch {
    // Telemetry must never interrupt application behavior.
  }
}

export function recordTelemetry(entry) {
  try {
    const entries = readStoredEntries()
    entries.push(
      fitEntry(
        toSerializable({
          timestamp: new Date().toISOString(),
          ...entry
        })
      )
    )
    writeStoredEntries(entries)
  } catch {
    // Telemetry must never interrupt application behavior.
  }
}

export function getTelemetryData() {
  return readStoredEntries()
}

export function clearTelemetryData() {
  try {
    getStorage()?.removeItem(TELEMETRY_STORAGE_KEY)
  } catch {
    // Ignore unavailable browser storage.
  }
}

function downloadTelemetry() {
  const blob = new Blob([JSON.stringify(getTelemetryData(), null, 2)], {
    type: 'application/json'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `agentnoon-telemetry-${new Date().toISOString().replaceAll(':', '-')}.json`
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function initializeTelemetryDownloadShortcut() {
  if (typeof globalThis.addEventListener !== 'function') return () => {}

  const handleKeydown = (event) => {
    const isDownloadShortcut =
      event.ctrlKey && event.altKey && event.shiftKey && event.code === 'KeyI' && !event.repeat

    if (!isDownloadShortcut) return
    event.preventDefault()
    downloadTelemetry()
  }

  globalThis.addEventListener('keydown', handleKeydown)
  return () => globalThis.removeEventListener('keydown', handleKeydown)
}

function isAllowedUrl(url) {
  try {
    const hostname = new URL(url, globalThis.location?.href).hostname.toLowerCase()
    return TELEMETRY_ALLOWED_HOST_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
    )
  } catch {
    return false
  }
}

function headersToObject(headers) {
  return Object.fromEntries(new Headers(headers).entries())
}

async function readFetchBody(message) {
  if (message instanceof Request && ['GET', 'HEAD'].includes(message.method)) return null

  try {
    return await message.clone().text()
  } catch {
    return '[unavailable]'
  }
}

function now() {
  return globalThis.performance?.now() ?? Date.now()
}

function instrumentFetch() {
  if (typeof globalThis.fetch !== 'function') return () => {}

  const originalFetch = globalThis.fetch
  const instrumentedFetch = async (input, init) => {
    const url = input instanceof Request ? input.url : String(input)
    if (!isAllowedUrl(url)) return originalFetch.call(globalThis, input, init)

    let request
    try {
      request = new Request(input, init)
    } catch {
      return originalFetch.call(globalThis, input, init)
    }

    const startedAt = now()
    const requestBody = readFetchBody(request)

    try {
      const response = await originalFetch.call(globalThis, input, init)
      const responseBody = readFetchBody(response)

      void Promise.all([requestBody, responseBody]).then(
        ([resolvedRequestBody, resolvedResponseBody]) => {
          recordTelemetry({
            type: 'network',
            method: request.method,
            url: request.url,
            requestHeaders: headersToObject(request.headers),
            requestBody: resolvedRequestBody,
            responseHeaders: headersToObject(response.headers),
            responseBody: resolvedResponseBody,
            status: response.status,
            statusText: response.statusText,
            durationMs: Math.round(now() - startedAt)
          })
        }
      )

      return response
    } catch (error) {
      void requestBody.then((resolvedRequestBody) => {
        recordTelemetry({
          type: 'network',
          method: request.method,
          url: request.url,
          requestHeaders: headersToObject(request.headers),
          requestBody: resolvedRequestBody,
          error,
          durationMs: Math.round(now() - startedAt)
        })
      })
      throw error
    }
  }

  globalThis.fetch = instrumentedFetch
  return () => {
    if (globalThis.fetch === instrumentedFetch) globalThis.fetch = originalFetch
  }
}

function parseResponseHeaders(rawHeaders) {
  const headers = {}
  rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .filter(Boolean)
    .forEach((line) => {
      const separator = line.indexOf(':')
      if (separator === -1) return
      headers[line.slice(0, separator).trim().toLowerCase()] = line.slice(separator + 1).trim()
    })
  return headers
}

function getXhrResponseBody(request) {
  if (!request.responseType || request.responseType === 'text') return request.responseText
  return request.response
}

function instrumentXMLHttpRequest() {
  const XMLHttpRequestClass = globalThis.XMLHttpRequest
  if (!XMLHttpRequestClass) return () => {}

  const requestDetails = new WeakMap()
  const originalOpen = XMLHttpRequestClass.prototype.open
  const originalSend = XMLHttpRequestClass.prototype.send
  const originalSetRequestHeader = XMLHttpRequestClass.prototype.setRequestHeader

  XMLHttpRequestClass.prototype.open = function (method, url, ...args) {
    requestDetails.set(this, {
      method: String(method).toUpperCase(),
      url: new URL(String(url), globalThis.location?.href).href,
      requestHeaders: {}
    })
    return originalOpen.call(this, method, url, ...args)
  }

  XMLHttpRequestClass.prototype.setRequestHeader = function (name, value) {
    const details = requestDetails.get(this)
    if (details) details.requestHeaders[name.toLowerCase()] = String(value)
    return originalSetRequestHeader.call(this, name, value)
  }

  XMLHttpRequestClass.prototype.send = function (body) {
    const details = requestDetails.get(this)
    if (details && isAllowedUrl(details.url)) {
      const startedAt = now()
      this.addEventListener(
        'loadend',
        () => {
          recordTelemetry({
            type: 'network',
            ...details,
            requestBody: toSerializable(body),
            responseHeaders: parseResponseHeaders(this.getAllResponseHeaders()),
            responseBody: toSerializable(getXhrResponseBody(this)),
            status: this.status,
            statusText: this.statusText,
            durationMs: Math.round(now() - startedAt)
          })
        },
        { once: true }
      )
    }
    return originalSend.call(this, body)
  }

  return () => {
    XMLHttpRequestClass.prototype.open = originalOpen
    XMLHttpRequestClass.prototype.send = originalSend
    XMLHttpRequestClass.prototype.setRequestHeader = originalSetRequestHeader
  }
}

export function initializeNetworkTelemetry() {
  const stopFetchTelemetry = instrumentFetch()
  const stopXhrTelemetry = instrumentXMLHttpRequest()

  return () => {
    stopFetchTelemetry()
    stopXhrTelemetry()
  }
}
